// ============================================
// Zen-AI-Pentest Platform - Network Graph Preview
// ============================================

import { useEffect, useRef } from 'react';
import * as d3 from 'd3';

interface Node {
  id: string;
  label: string;
  type: 'host' | 'service' | 'vulnerability';
  severity?: string;
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
  fx?: number | null;
  fy?: number | null;
}

interface Link {
  source: string | Node;
  target: string | Node;
  type: string;
}

export function NetworkGraphPreview() {
  const svgRef = useRef<SVGSVGElement>(null);

  useEffect(() => {
    if (!svgRef.current) return;

    const svg = d3.select(svgRef.current);
    svg.selectAll('*').remove();

    const width = svgRef.current.clientWidth;
    const height = 300;

    // Sample data
    const nodes: Node[] = [
      { id: '1', label: '192.168.1.1', type: 'host' },
      { id: '2', label: '192.168.1.2', type: 'host' },
      { id: '3', label: '192.168.1.3', type: 'host' },
      { id: '4', label: 'HTTP', type: 'service' },
      { id: '5', label: 'SSH', type: 'service' },
      { id: '6', label: 'MySQL', type: 'service' },
      { id: '7', label: 'CVE-2024-001', type: 'vulnerability', severity: 'critical' },
      { id: '8', label: 'CVE-2024-002', type: 'vulnerability', severity: 'high' },
      { id: '9', label: '192.168.1.4', type: 'host' },
      { id: '10', label: 'HTTPS', type: 'service' },
    ];

    const links: Link[] = [
      { source: '1', target: '4', type: 'has' },
      { source: '1', target: '5', type: 'has' },
      { source: '2', target: '6', type: 'has' },
      { source: '2', target: '7', type: 'vulnerable' },
      { source: '3', target: '4', type: 'has' },
      { source: '3', target: '8', type: 'vulnerable' },
      { source: '9', target: '10', type: 'has' },
      { source: '1', target: '2', type: 'connects' },
      { source: '2', target: '3', type: 'connects' },
      { source: '1', target: '9', type: 'connects' },
    ];

    const colorMap: Record<string, string> = {
      host: '#6366f1',
      service: '#10b981',
      vulnerability: '#ef4444',
    };

    const simulation = d3
      .forceSimulation<Node>(nodes)
      .force(
        'link',
        d3.forceLink<Node, Link>(links).id((d) => d.id).distance(80)
      )
      .force('charge', d3.forceManyBody().strength(-300))
      .force('center', d3.forceCenter(width / 2, height / 2))
      .force('collision', d3.forceCollide().radius(30));

    // Draw links
    const link = svg
      .append('g')
      .selectAll('line')
      .data(links)
      .enter()
      .append('line')
      .attr('stroke', '#94a3b8')
      .attr('stroke-opacity', 0.6)
      .attr('stroke-width', 1);

    // Draw nodes
    const node = svg
      .append('g')
      .selectAll('g')
      .data(nodes)
      .enter()
      .append('g')
      .call(d3.drag<SVGGElement, Node>()
        .on('start', (event, d) => {
          if (!event.active) simulation.alphaTarget(0.3).restart();
          d.fx = d.x;
          d.fy = d.y;
        })
        .on('drag', (event, d) => {
          d.fx = event.x;
          d.fy = event.y;
        })
        .on('end', (event, d) => {
          if (!event.active) simulation.alphaTarget(0);
          d.fx = null;
          d.fy = null;
        })
      );

    // Node circles
    node
      .append('circle')
      .attr('r', (d) => (d.type === 'host' ? 20 : d.type === 'service' ? 15 : 12))
      .attr('fill', (d) => colorMap[d.type])
      .attr('stroke', '#fff')
      .attr('stroke-width', 2)
      .style('cursor', 'pointer');

    // Node labels
    node
      .append('text')
      .text((d) => d.label)
      .attr('x', 0)
      .attr('y', (d) => (d.type === 'host' ? 35 : d.type === 'service' ? 30 : 25))
      .attr('text-anchor', 'middle')
      .attr('font-size', '10px')
      .attr('fill', 'currentColor')
      .style('pointer-events', 'none');

    // Update positions on tick
    simulation.on('tick', () => {
      link
        .attr('x1', (d) => (d.source as Node).x!)
        .attr('y1', (d) => (d.source as Node).y!)
        .attr('x2', (d) => (d.target as Node).x!)
        .attr('y2', (d) => (d.target as Node).y!);

      node.attr('transform', (d) => `translate(${d.x},${d.y})`);
    });

    return () => {
      simulation.stop();
    };
  }, []);

  return (
    <div className="w-full h-[300px] relative">
      <svg ref={svgRef} width="100%" height="100%" className="overflow-visible" />
      
      {/* Legend */}
      <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm rounded-lg p-2 text-xs border">
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-indigo-500" />
          <span>Host</span>
        </div>
        <div className="flex items-center gap-2 mb-1">
          <div className="w-3 h-3 rounded-full bg-emerald-500" />
          <span>Service</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span>Vulnerability</span>
        </div>
      </div>
    </div>
  );
}
